'use client'

import { useState, memo } from 'react'
import {
  UserIcon,
  WaveformIcon,
  TagIcon,
  GlobeIcon,
} from '@phosphor-icons/react'
import { ImageWithFallback } from '@/ui/image-with-fallback'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { MarkdownContent } from '@/ui/markdown-content'
import { formatRelativeTime } from '@/lib/date'
import {
  ExpandableRow,
  RowHeader,
  RowContent,
  RowActions,
  PeopleList,
  TopicBadges,
  TypeBadge,
  MetaTagsRow,
  MetaChip,
} from '@/feed/row-chrome'
import type { RowProps } from '../../types'
import type { LabelEntry } from '../meta'
import type { MediaDraft } from '../write-spec'
import { SourceLink } from '@/feed/row-chrome'
import { LabelEditForm } from './EditForms'

interface LabelMetadata {
  imageUrl?: string
  website?: string
  profile?: string
}

type LabelRowProps = RowProps<LabelEntry, MediaDraft>

export const LabelRow = memo(
  function LabelRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
  }: LabelRowProps) {
    const metadata = entry.metadata as LabelMetadata
    const people = entry.people || []
    const topics = entry.topics || []
    const [isEditing, setIsEditing] = useState(false)
    const [editName, setEditName] = useState(entry.title)
    const [editWebsite, setEditWebsite] = useState(metadata.website || '')
    const [editProfile, setEditProfile] = useState(metadata.profile || '')
    const [editNotes, setEditNotes] = useState(entry.content || '')
    const [editTopics, setEditTopics] = useState(
      topics?.map((t) => t.name).join(', ') || '',
    )

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditName(entry.title)
      setEditWebsite(metadata.website || '')
      setEditProfile(metadata.profile || '')
      setEditNotes(entry.content || '')
      setEditTopics(topics?.map((t) => t.name).join(', ') || '')
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      const topicsArray = editTopics
        .split(',')
        .map((g: string) => g.trim())
        .filter(Boolean)
      await onSave({
        name: editName,
        website: editWebsite || null,
        profile: editProfile || null,
        notes: editNotes || null,
        topics: topicsArray.length > 0 ? topicsArray : undefined,
      })
      setIsEditing(false)
    }

    const header = (
      <RowHeader
        onToggle={onToggle}
        isExpanded={isExpanded}
        icon={
          <WaveformIcon
            className="text-entry-music h-5 w-5 sm:h-4 sm:w-4"
            weight="duotone"
          />
        }
        title={entry.title}
        titleSuffix={
          topics.length > 0 && (
            <span className="hidden sm:inline">
              <TopicBadges topics={topics} maxDisplay={2} />
            </span>
          )
        }
        metadata={
          <span className="text-muted-foreground/50 hidden text-xs sm:inline">
            {formatRelativeTime(entry.createdAt)}
          </span>
        }
      />
    )

    return (
      <ExpandableRow isExpanded={isExpanded} entryType="label" header={header}>
        <RowContent
          showImage
          image={
            <ImageWithFallback
              src={entry.imageUrl}
              alt={entry.title}
              aspectRatio="square"
              fallbackIcon={WaveformIcon}
              fallbackColor="text-entry-music"
            />
          }
        >
          {isEditing ? (
            <LabelEditForm
              editName={editName}
              setEditName={setEditName}
              editWebsite={editWebsite}
              setEditWebsite={setEditWebsite}
              editProfile={editProfile}
              setEditProfile={setEditProfile}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              editTopics={editTopics}
              setEditTopics={setEditTopics}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            <>
              {metadata.profile && (
                <p className="text-muted-foreground mb-2 line-clamp-2 text-sm sm:line-clamp-3">
                  {metadata.profile}
                </p>
              )}

              {entry.content && (
                <MarkdownContent className="text-muted-foreground mb-2 text-sm italic">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Read-only meta chips */}
              <MetaTagsRow className="mb-2">
                <TypeBadge type="Label" entryType="label" />
                {metadata.website && (
                  <MetaChip icon={<GlobeIcon className="h-3 w-3" />}>
                    {metadata.website}
                  </MetaChip>
                )}
                {topics.length > 0 &&
                  topics.map((topic) => (
                    <MetaChip
                      key={topic.id}
                      icon={<TagIcon className="h-3 w-3" />}
                    >
                      {topic.name}
                    </MetaChip>
                  ))}
              </MetaTagsRow>

              {people.length > 0 && (
                <div className="mb-2">
                  <PeopleList
                    people={people}
                    icon={<UserIcon className="h-3 w-3" />}
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions className="justify-end">
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </RowContent>
      </ExpandableRow>
    )
  },
  (prevProps, nextProps) =>
    prevProps.entry.id === nextProps.entry.id &&
    prevProps.entry.title === nextProps.entry.title &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.isSaving === nextProps.isSaving &&
    prevProps.entry.url === nextProps.entry.url,
)
